import { Trash } from '@phosphor-icons/react';
import { cloneElement, ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Confirmation } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { ICorporatePricingProfile } from 'pricing/types';
import { useProjectPricingProfileActions } from 'projects/hooks';
import { Color, Variant } from 'theme/enums';

interface IProps {
  buttonElement?: ReactElement;
  buttonLabel?: string;
  notificationPosition?: NotificationPosition;
  onSuccess?: () => void;
  pricingProfiles: ICorporatePricingProfile[];
  projectId: number;
}

export const UnlinkProjectPricingProfileButton = ({
  buttonElement,
  buttonLabel,
  notificationPosition = NotificationPosition.TOP,
  onSuccess,
  pricingProfiles,
  projectId,
}: IProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const { t } = useTranslation();

  const { unlinkProjectPricingProfile } = useProjectPricingProfileActions({ notificationPosition });

  const onRemove = useCallback(async () => {
    setIsConfirmationOpen(false);

    const { success } = await unlinkProjectPricingProfile({
      ids: pricingProfiles.map((p) => p.id),
      project: projectId,
    });

    if (!success) {
      return;
    }

    onSuccess?.();
  }, [onSuccess, pricingProfiles, projectId, unlinkProjectPricingProfile]);

  const button = useMemo(() => {
    if (buttonElement) {
      return cloneElement(buttonElement, {
        onClick: () => setIsConfirmationOpen(true),
      });
    }

    return (
      <Button
        text={buttonLabel ?? t('buttonLabelRemove')}
        color={Color.DANGER}
        variant={Variant.SOFT}
        startDecorator={<Trash />}
        onClick={() => setIsConfirmationOpen(true)}
      />
    );
  }, [buttonElement, buttonLabel, t]);

  return (
    <>
      {button}

      <Confirmation
        isOpen={isConfirmationOpen}
        title={t('titleRemoveProjectPricingProfile', {
          count: pricingProfiles.length,
        })}
        message={t('messageConfirmRemoveProjectPricingProfile', {
          count: pricingProfiles.length,
          name: pricingProfiles[0]?.name,
        })}
        confirmBtnLabel={t('buttonLabelRemove')}
        confirmBtnColor={Color.DANGER}
        onConfirm={onRemove}
        onCancel={() => setIsConfirmationOpen(false)}
      />
    </>
  );
};
